<template>
  <div :style="style.container">
    <DialogDeleteSurveyPrimary @delete-survey="deleteSurvey" />
    <ModalSurveyPublishPrimary @publish-survey="publishSurvey" />
    <ModalSurveyRenamePrimary @rename-survey="updateSurveyName" />
    <ModalSurveySettingsPrimary @update-survey-expiration="updateSurveyExpiration" />
    <ModalSurveySharePrimary />
    <router-view
      v-if="initialized"
      :key="$route.fullPath"
      :survey="survey"
      :surveyCategorySkills="surveyCategorySkills"
      :tenantUser="tenantUser"
    />
  </div>
</template>

<script>
  import ApiSurvey from '@js/api/survey'
  import ApiSurveyCategorySkill from '@js/api/survey-category-skill'
  import ApiSurveyPublish from '@js/api/survey-publish'
  import DialogDeleteSurveyPrimary
    from '@survey/dialogs/dialog-delete-survey-primary'
  import ModalSurveyPublishPrimary
    from '@survey/modals/modal-survey-publish-primary'
  import ModalSurveyRenamePrimary
    from '@survey/modals/modal-survey-rename-primary'
  import ModalSurveySettingsPrimary
    from '@survey/modals/modal-survey-settings-primary'
  import ModalSurveySharePrimary
    from '@survey/modals/modal-survey-share-primary'
  import { DIALOG_NAMES, MODAL_WINDOW_NAMES } from '@js/constants'
  import { mapGetters, mapState } from 'vuex'

  export default {
    name: 'AdminSurveyRootView',
    components: {
      DialogDeleteSurveyPrimary,
      ModalSurveyPublishPrimary,
      ModalSurveyRenamePrimary,
      ModalSurveySettingsPrimary,
      ModalSurveySharePrimary,
    },
    data: function () {
      return {
        initialized: false,
        survey: {},
        surveyCategorySkills: [],
        surveyCode: '',
        style: {
          container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            width: '100%',
          },
        },
      };
    },
    created: function () {
      if (
        !this.isAdmin ||
        !this.isEnterprise
      ) {
        this.$store.commit('notification/setMessage', 'Access denied');
        this.$router.push({
          name: 'private-home',
        });
      }

      const surveyCode = this.$route.params.surveyCode;
      if (
        surveyCode === undefined ||
        surveyCode === null ||
        surveyCode.length === 0
      ) {
        this.$store.commit('notification/setWarning', 'Survey access denied');
        this.$router.push({
          name: 'private-home',
        });
      }
      this.surveyCode = surveyCode;
    },
    mounted: async function () {
      await this.fetchSurvey();
      await this.fetchSurveyCategorySkills();
      this.initialized = await true;
    },
    computed: {
      ...mapGetters('tenant', ['isEnterprise']),
      ...mapGetters('tenantUser', ['isAdmin']),
      ...mapState({
        tenantUser: state => state.tenantUser,
      }),
    },
    methods: {
      deleteSurvey: async function () {
        try {
          const api = await new ApiSurvey(this.survey.code);
          const res = await api.delete();
          await this.$store.commit(
            'notification/setMessage',
            `Deleted ${res.data.name}`,
          );
          await this.$modal.hide(DIALOG_NAMES.DELETE_SURVEY_PRIMARY);
          await this.$router.push({
            name: 'admin-surveys',
          });
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to delete survey',
          );
        }
      },
      fetchSurvey: async function () {
        try {
          const api = await new ApiSurvey(this.surveyCode);
          const res = await api.get();
          this.survey = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch survey',
          );
          this.$router.push({
            name: 'admin-surveys',
          });
        }
      },
      fetchSurveyCategorySkills: async function () {
        try {
          const api = await new ApiSurveyCategorySkill(this.survey.id);
          const res = await api.get();
          this.surveyCategorySkills = await res.data;
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to fetch skills',
          );
          this.$router.push({
            name: 'admin-surveys',
          });
        }
      },
      publishSurvey: async function (expiration) {
        try {
          const api = await new ApiSurveyPublish(this.survey.code);
          const payload = await {
            expirationType: expiration.type,
          };
          const res = await api.put(payload);
          this.survey = await res.data;
          await this.$store.commit(
            'notification/setMessage',
            `Published ${this.survey.name}`
          );
          await this.$modal.hide(MODAL_WINDOW_NAMES.PUBLISH_SURVEY_PRIMARY);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to publish survey',
          );
        }
      },
      updateSurveyExpiration: async function (expiration) {
        try {
          const api = await new ApiSurveyPublish(this.survey.code);
          const payload = await {
            expirationType: expiration.type,
          };
          const res = await api.put(payload);
          this.survey = await res.data;
          await this.$store.commit(
            'notification/setMessage',
            `Published ${this.survey.name}`
          );
          await this.$modal.hide(MODAL_WINDOW_NAMES.CONFIGURE_SURVEY_PRIMARY);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to publish survey',
          );
        }
      },
      updateSurveyName: async function (renamedSurvey) {
        try {
          const payload = {
            name: renamedSurvey.name,
          };
          const api = await new ApiSurvey(renamedSurvey.code);
          const res = await api.put(payload);
          this.survey.name = await res.data.name;
          await this.$store.commit(
            'notification/setMessage',
            `Renamed survey to ${this.survey.name}`,
          );
          await this.$modal.hide(MODAL_WINDOW_NAMES.RENAME_SURVEY_PRIMARY);
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to rename survey',
          );
        }
      },
    },
  };
</script>
